import { add, mergeWith, cloneDeep } from 'lodash';

/** Array: Array of all data properties needed */
/** Object: Contains all of the charts with their categories */
export const neededProperties = [
  'createdActions',
  'finishedActions',
  'actionCreatingTime',
  'createdPb',
  'finishedPb',
  'pbCreatingTime',
  'bugsReported',
  'tasksReported',
  'login',
  'time',
  'finishedMeetings',
];

/**
 * Creates an array with all dates between a range of dates
 * @param  {Object} start - Moment object with the start date
 * @param  {Object} end - Moment object with the end date
 * @return {Array} - array of dates
 */
export function datesBetweenRange(start, end) {
  const dates = [];
  while (start.isSameOrBefore(end)) {
    dates.push(start.format('YYYY-MM-DD'));
    start.add(1, 'days');
  }
  return dates;
}

/**
 * Create an object with the creating time from an array of data
 * @param  {Array} itemsArray - Array of data
 * @param  {String} currentDate - Current date
 * @return {Object} - Object with current day creating time
 */
export function getCreatingTimePerDate(itemsArray, currentDate) {
  let creatingTime = 0;
  let nCreatedTimeItem = 0;

  (itemsArray || []).forEach(({ endOfCreation, createdDay, created }) => {
    if (endOfCreation && createdDay === currentDate) {
      const seconds = (new Date(endOfCreation) - new Date(created)) / 1000;
      creatingTime += seconds;
      nCreatedTimeItem++;
    }
  });
  if (creatingTime > 0) creatingTime /= nCreatedTimeItem;
  return {
    reported: currentDate,
    value: creatingTime,
  };
}

/**
 * Return the average creation time of a problem or an action
 * @param  {Object} creatingTimeObj - Object who contain objects who contains a "creationTime" property
 * @return {Number} - average creation time
 */
export function getAverageTime(creatingTimeObj) {
  let creationTime = 0;
  let nCreationTime = 0;

  Object.values(creatingTimeObj).forEach(({ value }) => {
    if (value > 0) {
      creationTime += value;
      nCreationTime++;
    }
  });

  if (creationTime > 0) {
    return creationTime / nCreationTime;
  }
  return 0;
}

/**
 * Merge into an object all data from teams / users
 * @param  {Array} childArr - Array of users / teams
 * @param  {String} key - The property you want to get
 * @return {Object} - Object with merged data
 */
export function getDataFromChildren(childArr, key) {
  let dataObj = {};
  childArr.forEach((child) => {
    if (Object.keys(dataObj).length) {
      dataObj = mergeWith(dataObj, child.data[key], ((objValue, srcValue) => {
        if (typeof objValue === 'number') {
          return add(objValue, srcValue);
        }
      }));
    } else {
      dataObj = cloneDeep(child.data[key]);
    }
  });
  return dataObj;
}

/**
 * Creates an object with time spended for the current date
 * @param  {Array} timeObj - Array of activity date
 * @param  {String} currentDate - Current date
 * @return {Object} - Object with current day data
 */
export function getTimePerDate(timeObj, currentDate) {
  let onlineTime = 0;
  let afkTime = 0;
  let lastOnline = null;
  (timeObj || []).forEach((element, index) => {
    if (element.dateDay === currentDate) {
      if (Number(element.isActive) === 1) {
        if (lastOnline) {
          const seconds = (new Date(element.date) - new Date(lastOnline)) / 1000;
          if (seconds >= 1200) {
            afkTime += seconds;
            lastOnline = element.date;
          } else {
            onlineTime += seconds;
            lastOnline = element.date;
          }
        } else {
          lastOnline = element.date;
        }
      }
      if (timeObj.length - 1 === index && timeObj[timeObj.length - 1].isActive === 0) {
        const seconds = (new Date(element.date) - new Date(lastOnline)) / 1000;
        afkTime += seconds;
      }
    }
  });
  return {
    reported: currentDate,
    value: {
      onlineTime,
      afkTime,
    },
  };
}

/**
 * Sum properties in one
 * @param  {Object} ObjToSum - Object of data you want to sum
 * @param  {String} key - The property key you want to get
 * @param  {String} [secondKey] - Optional other property you need
 * @return {Number} - result of properties sum
 */
export function sumProps(ObjToSum, key, secondKey) {
  let sumResult = 0;
  Object.values(ObjToSum).forEach((val) => {
    if (secondKey) {
      sumResult += val[key][secondKey];
    } else {
      sumResult += val[key];
    }
  });
  return sumResult;
}

/**
 * Format an array of data like teams or companies
 * @param  {Array} dataArray - Array of data object
 * @param  {Array} dates - Array of choosen dates
 * @return {Array} - Formatted array of data
 */
export function formatDataFromCompany(dataArray, dates) {
  (dataArray || []).forEach((element) => {
    const {
      actions = [],
      problems = [],
      time = [],
      login = [],
      issuesHistory = [],
      finishedMeetings = [],
    } = element;
    const dataProps = {};
    neededProperties.forEach((prop) => {
      dataProps[prop] = {};
    });
    dates.forEach((date) => {
      Object.keys(dataProps).forEach((propKey) => {
        if (propKey !== 'time') {
          dataProps[propKey][date] = { reported: date, value: 0 };
        }
      });
      dataProps.pbCreatingTime[date] = getCreatingTimePerDate(problems, date);
      dataProps.actionCreatingTime[date] = getCreatingTimePerDate(actions, date);
      dataProps.time[date] = getTimePerDate(time, date);
    });
    problems.forEach(({ createdDay, actedOnDay, state }) => {
      if (dataProps.createdPb[createdDay]) {
        dataProps.createdPb[createdDay].value++;
      }
      if (Number(state) === 4) {
        if (dataProps.finishedPb[actedOnDay]) {
          dataProps.finishedPb[actedOnDay].value++;
        }
      }
    });
    actions.forEach(({ createdDay, actedOnDay }) => {
      if (dataProps.createdActions[createdDay]) {
        dataProps.createdActions[createdDay].value++;
      }
      if (dataProps.finishedActions[actedOnDay]) {
        dataProps.finishedActions[actedOnDay].value++;
      }
    });
    finishedMeetings.forEach(({ endedDay }) => {
      if (dataProps.finishedMeetings[endedDay]) {
        dataProps.finishedMeetings[endedDay].value++;
      }
    });
    issuesHistory.forEach(({ createdDay, issueType }) => {
      if (issueType === 'Bug') {
        dataProps.bugsReported[createdDay].value++;
      } else {
        dataProps.tasksReported[createdDay].value++;
      }
    });
    login.forEach(({ loginDateDay }) => {
      dataProps.login[loginDateDay].value++;
    });
    element.data = {};
    neededProperties.forEach((prop) => {
      element.data[prop] = dataProps[prop];
    });
    element.sumNActionsCreated = sumProps(element.data.createdActions, 'value');
    element.sumNActionsFinished = sumProps(element.data.finishedActions, 'value');
    element.sumNPbCreated = sumProps(element.data.createdPb, 'value');
    element.sumNPbFinished = sumProps(element.data.finishedPb, 'value');
    if (Object.keys(element.data.time).length) {
      element.sumOnlineTime = sumProps(element.data.time, 'value', 'onlineTime');
      element.sumAfkTime = sumProps(element.data.time, 'value', 'afkTime');
    } else {
      element.sumOnlineTime = 0;
      element.sumAfkTime = 0;
    }
    element.sumActionCreatingTime = getAverageTime(element.data.actionCreatingTime);
    element.sumPbCreatingTime = getAverageTime(element.data.pbCreatingTime);
    element.sumNConnection = sumProps(element.data.login, 'value');
    element.sumNBugs = sumProps(element.data.bugsReported, 'value');
    element.sumNTasks = sumProps(element.data.tasksReported, 'value');
  });
  return dataArray;
}

/**
 * Format a choosen user
 * @param  {Object} user - User object
 * @param  {Array} dates - Array of choosen dates
 * @return {Object} - Formatted user object
 */
export function formatCurrentUser(user, dates) {
  const { time, login } = user;
  const timeFormat = {};
  const loginFormat = {};
  dates.forEach((date) => {
    loginFormat[date] = { reported: date, value: 0 };
    timeFormat[date] = getTimePerDate(time, date);
  });
  login.forEach(({ loginDateDay }) => {
    loginFormat[loginDateDay].value++;
  });
  user.data = {
    time: timeFormat,
    login: loginFormat,
  };
  user.sumOnlineTime = sumProps(user.data.time, 'value', 'onlineTime');
  user.sumAfkTime = sumProps(user.data.time, 'value', 'afkTime');
  user.sumNConnection = sumProps(user.data.login, 'value');
  return user;
}

/**
 * Format an array of users
 * @param  {Array} users - Array of users
 * @param  {Array} dates - Array of choosen dates
 * @return {Array} - Array of formatted users
 */
export function formatUsers(users, dates) {
  const usersArray = [];
  users.forEach((user) => {
    usersArray.push(formatCurrentUser(user, dates));
  });
  return users;
}

export const timeStepSizes = [
  { max: 8640000, stepSize: 1728000, unitFormat: 'day' },
  { max: 1728000, stepSize: 864000, unitFormat: 'day' },
  { max: 864000, stepSize: 172800, unitFormat: 'day' },
  { max: 172800, stepSize: 86400, unitFormat: 'day' },
  { max: 21600, stepSize: 10800, unitFormat: 'hours' },
  { max: 3600, stepSize: 1800, unitFormat: 'hours' },
  { max: 600, stepSize: 300, unitFormat: 'minutes' },
  { max: 60, stepSize: 30, unitFormat: 'seconds' },
];


export default {
  datesBetweenRange,
  formatCurrentUser,
  formatDataFromCompany,
  formatUsers,
  getAverageTime,
  getCreatingTimePerDate,
  getDataFromChildren,
  getTimePerDate,
  neededProperties,
  timeStepSizes,
  sumProps,
};
